import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories, siteInfo } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-2">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700" onClick={handleClick}>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `${siteInfo.url}/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 明星黑料大全 | ${category.description}`}
        description={`${category.name}——${category.description}。明星黑料大全为您提供最新最全的${category.name}资讯。`}
        keywords={`${category.name},明星黑料,娱乐圈黑料,明星丑闻,艺人内幕`}
        canonicalUrl={`${siteInfo.url}/category/${category.slug}`}
        schemaJson={categorySchema}
      />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-foreground-950 text-background-50 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/20 mx-auto mb-4">
              <i className={`${category.icon} text-2xl text-primary-400`}></i>
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl mb-2">{category.name}</h1>
            <p className="text-foreground-400 max-w-xl mx-auto text-sm">
              {category.description}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />

          {/* Category Tabs */}
          <div className="flex items-center flex-wrap gap-2 mb-8 pb-6 border-b border-background-200/70">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={handleClick}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="default" />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}